import { Badge, Col, FormGroup, FormLabel, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import InputMoney from "../../../components/Forms/Element/InputMoney";
import AsteriskRequired from "../../../components/Common/AsteriskRequired";
import { useBranch } from "../../../../context/BranchContext";
import BranchSelect from "../../../components/Custom/Main/Branch/BranchSelect";
import { useEffect, useState } from "react";
import InputText from "../../../components/Forms/Element/InputText";
import { useLoggedUser } from "../../../../context/LoggedUserContext";
import InputDateTime from "../../../components/Forms/Element/InputDateTime";
import TransactionCashFlowTypeSelect from "../../../components/Custom/CashFlow/Transaction/TransactionCashFlowTypeSelect";
import FormSection from "../../../components/Common/Form/FormSection";
import HintTooltip from "../../../components/Common/HintTooltip";

const Form = (props) => {

    const { t } = useTranslation();
    const { formik, resetForm } = props;
    const { selectedBranch } = useBranch();
    const [registeredBy, setRegisteredBy] = useState('');
    const { loggedUser } = useLoggedUser();
    const { setFieldValue } = formik;

    // Desablita o campo quando for selecionado uma filial no contexto.
    // Se tiver marcado como todas, habilita e exibe todas as filiais para que o usuário escolha.
    const [disabledBranch, setDisabledBranch] = useState(false);

    useEffect(() => {
        if (selectedBranch === null) {
            setDisabledBranch(false);
            setFieldValue('branch', null);
        } else {
            // Deixa marcado a filial que está no contexto.
            setFieldValue('branch', {
                label: selectedBranch.name,
                value: selectedBranch.id
            });
            setDisabledBranch(true);
        }
    }, [resetForm, selectedBranch, setFieldValue]);

    useEffect(() => {
        if (loggedUser !== null) {
            setRegisteredBy(loggedUser.firstName);
        }
    }, [loggedUser]);

    // O relógio ao vivo saiu do cabeçalho quando a data virou um campo editável: manter os dois
    // faria a tela mostrar dois horários concorrentes, e o que vale é o que será gravado.
    const transactionStatus = (
        <div className="d-flex flex-column align-items-start align-items-md-end gap-2">
            {formik.values.type && (
                <Badge bg={formik.values.type.value === 'OUT' ? 'danger' : 'success'} className="px-3 py-2">
                    <i className={`fas fa-${formik.values.type.value === 'OUT' ? 'minus' : 'plus'}-circle me-1`}></i>
                    {formik.values.type.value === 'OUT' ? t('cash_flow.expense') : t('cash_flow.income')}
                </Badge>
            )}
        </div>
    );

    return (
        <>
            <div className="form-layout form-layout--spaced">
                <FormSection
                    title={t('cash_flow.new_transaction')}
                    description={t('cash_flow.cash_transaction_information_hint')}
                    icon="fa-solid fa-right-left"
                    actions={transactionStatus}
                >
                    <Row className="g-3">
                        <Col md={4}>
                            <FormLabel>{t('registered_by')}</FormLabel>
                            <InputText
                                name="registeredBy"
                                value={registeredBy}
                                disabled={true} />
                        </Col>
                        <Col md={4}>
                            <FormLabel>
                                {t('cash_flow.transaction_time')} <AsteriskRequired />
                                <HintTooltip text={t('cash_flow.transaction_time_hint')} />
                            </FormLabel>
                            <InputDateTime
                                name="transactionDate"
                                selected={formik.values.transactionDate}
                                onChange={(value) =>
                                    formik.setFieldValue('transactionDate', value)
                                }
                                onBlur={formik.handleBlur}
                                error={formik.errors.transactionDate}
                                maxDate={new Date()} />
                        </Col>
                        <Col md={4}>
                            <FormLabel>{t('branch')} <AsteriskRequired/></FormLabel>
                            <BranchSelect
                                name="branch"
                                value={formik.values.branch}
                                onChange={(value) =>
                                    formik.setFieldValue('branch', value)
                                }
                                onBlur={formik.handleBlur}
                                error={formik.errors.branch}
                                isDisabled={disabledBranch} />
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <FormLabel>
                                    {t('type')} <AsteriskRequired />
                                    <HintTooltip text={t('cash_flow.transaction_type_hint')} />
                                </FormLabel>
                                <TransactionCashFlowTypeSelect
                                    onChange={(value) =>
                                        formik.setFieldValue('type', value)
                                    }
                                    value={formik.values.type}
                                    onBlur={formik.handleBlur}
                                    error={formik.errors.type}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <FormLabel>
                                    {t('value')} ({t('cash')}) <AsteriskRequired />
                                    <HintTooltip text={t('cash_flow.transaction_value_hint')} />
                                </FormLabel>
                                <InputMoney
                                    name="value"
                                    value={formik.values.value}
                                    onChange={(value) => formik.setFieldValue('value', value)}
                                    onBlur={formik.handleBlur}
                                    error={formik.errors.value}
                                    style={{ fontWeight: 'bold', fontSize: '1.05rem' }} />
                            </FormGroup>
                        </Col>
                    </Row>
                </FormSection>

                <FormSection
                    compact
                    title={t('description')}
                    description={t('cash_flow.cash_transaction_description_hint')}
                    icon="fa-solid fa-note-sticky"
                >
                    <Row>
                        <Col md={12}>
                            <InputText name="description"
                                maxLength={50}
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.errors.description}
                                placeholder={t('cash_flow.transaction_description_placeholder')}
                            />
                        </Col>
                    </Row>
                </FormSection>
            </div>
        </>
    );
}

export default Form;
